import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BookRecommendation } from "./useBookRecommendations";

// Normalize title for comparison
const normalizeTitle = (title: string) => 
  title.toLowerCase().replace(/[^a-z0-9]/g, '').trim();

// Check if title is a box set or collection
const isBoxSetOrCollection = (title: string) => {
  const t = (title || '').toLowerCase();
  return t.includes('box set') || 
         t.includes('boxed set') ||
         t.includes('books set') ||
         t.includes('book set') ||
         t.match(/set of \d+ books/i) !== null ||
         t.match(/\(\d+ books?\)/i) !== null ||
         t.match(/\d+ books? (set|collection|series)/i) !== null ||
         t.match(/complete (series|collection|set)/i) !== null ||
         t.match(/series collection/i) !== null ||
         t.match(/series set/i) !== null;
};

// Known series patterns for filtering out series user has already read
const KNOWN_SERIES_NAMES = [
  'throne of glass', 'court of thorns', 'crescent city', 'acotar', 
  'a court of', 'harry potter', 'hunger games', 'divergent',
  'twilight', 'percy jackson', 'game of thrones', 'lord of the rings',
  'shadow and bone', 'six of crows', 'red queen', 'the selection',
  'from blood and ash', 'shatter me', 'the cruel prince',
  'iron fey', 'mortal instruments', 'infernal devices', 'dark artifices',
  'fourth wing', 'house of earth', 'house of sky', 'house of flame',
  'bridgerton', 'outlander', 'red rising', 'mistborn', 'stormlight',
  'wheel of time', 'first law', 'gentlemen bastard', 'kingkiller',
  'dune', 'foundation', 'expanse', 'ender', 'dark tower'
];

interface CachedRecommendation {
  id: string;
  book_id: string | null;
  book_title: string;
  book_author: string | null;
  cover_url: string | null;
  reason: string | null;
  recommendation_type: string | null;
  source_books: any;
  confidence_score: number | null;
  status: string;
  created_at: string;
}

export const useCachedRecommendations = (limit: number = 6) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['cachedRecommendations', limit],
    queryFn: async (): Promise<{ 
      recommendations: BookRecommendation[], 
      classicRecommendation: BookRecommendation | null,
      fromCache: boolean,
      cacheCount: number,
      needsMoreReviews?: boolean,
      reviewCount?: number,
      requiredCount?: number
    }> => {
      console.log('🔍 Fetching cached recommendations...');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Check if user has minimum reviews before fetching recommendations
      const { count: reviewCount } = await supabase
        .from('user_books')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .not('rating_star', 'is', null)
        .gt('rating_star', 0);

      const MIN_REVIEWS = 3;
      if ((reviewCount || 0) < MIN_REVIEWS) {
        console.log(`⏸️ User has ${reviewCount || 0} reviews, need ${MIN_REVIEWS} for recommendations`);
        return {
          recommendations: [],
          classicRecommendation: null,
          fromCache: false,
          cacheCount: 0,
          needsMoreReviews: true,
          reviewCount: reviewCount || 0,
          requiredCount: MIN_REVIEWS
        };
      }

      // Fetch pending recommendations from cache
      const { data: cachedRecs, error: cacheError } = await supabase
        .from('user_cached_recommendations')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(limit + 5); // +5 for potential classic + safety margin

      if (cacheError) {
        console.error('❌ Cache query error:', cacheError);
        throw cacheError;
      }

      // Fetch current user library titles and detect series they've read
      const { data: tbrBooks } = await supabase
        .from('user_books')
        .select('books!user_books_book_id_fkey(title, series_name)')
        .eq('user_id', session.user.id)
        .in('shelf', ['to-read', 'reading', 'read', 'finished']);

      const excludedTitles = new Set(
        (tbrBooks || [])
          .map((ub: any) => normalizeTitle(ub.books?.title || ''))
          .filter(Boolean)
      );

      // Detect which series the user has read
      const userSeriesNames = new Set<string>();
      (tbrBooks || []).forEach((ub: any) => {
        const title = (ub.books?.title || '').toLowerCase();
        const seriesName = (ub.books?.series_name || '').toLowerCase();
        
        // Add explicit series name from DB
        if (seriesName) {
          userSeriesNames.add(seriesName);
        }
        
        // Also check known series patterns in titles
        for (const pattern of KNOWN_SERIES_NAMES) {
          if (title.includes(pattern)) {
            userSeriesNames.add(pattern);
          }
        }
      });

      console.log(`🛡️ Client-side safety net: ${excludedTitles.size} titles, ${userSeriesNames.size} series to exclude`);

      const cacheCount = cachedRecs?.length || 0;
      console.log(`📦 Found ${cacheCount} cached recommendations`);

      // If we have cached recs, use them
      if (cachedRecs && cachedRecs.length > 0) {
        const sessionId = crypto.randomUUID();
        
        // Check if rec is AI-generated (has source_books or personalized reason)
        const isAIGenerated = (rec: CachedRecommendation) => {
          if (rec.source_books && Array.isArray(rec.source_books) && rec.source_books.length > 0) return true;
          if (!rec.reason) return false;
          const genericPhrases = ['A popular book', 'stood the test of time', 'reader favorites'];
          return !genericPhrases.some(phrase => rec.reason?.includes(phrase));
        };
        
        // Sort: AI-generated first, then by date
        const sortedRecs = [...cachedRecs].sort((a, b) => {
          const aIsAI = isAIGenerated(a);
          const bIsAI = isAIGenerated(b);
          if (aIsAI && !bIsAI) return -1;
          if (!aIsAI && bIsAI) return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        // Convert cached recs to BookRecommendation format
        // Filter out books already in library, box sets, and series user has read
        const recommendations = sortedRecs
          .filter((rec: CachedRecommendation) => {
            const normTitle = normalizeTitle(rec.book_title);
            const titleLower = (rec.book_title || '').toLowerCase();
            
            // Exact title match - already in library
            if (excludedTitles.has(normTitle)) {
              console.log(`🛡️ Filtered (already in library): "${rec.book_title}"`);
              return false;
            }
            
            // Box set filter
            if (isBoxSetOrCollection(rec.book_title)) {
              console.log(`🛡️ Filtered (box set): "${rec.book_title}"`);
              return false;
            }
            
            // Series filter - check if rec is from a series user has already read
            for (const seriesName of userSeriesNames) {
              if (titleLower.includes(seriesName)) {
                console.log(`🛡️ Filtered (read ${seriesName} series): "${rec.book_title}"`);
                return false;
              }
            }
            
            return true;
          })
          .map((rec: CachedRecommendation): BookRecommendation => ({
            title: rec.book_title,
            author: rec.book_author || 'Unknown',
            reason: rec.reason || '',
            bookId: rec.book_id || undefined,
            coverUrl: rec.cover_url || undefined,
            confidenceScore: rec.confidence_score || undefined,
            recommendationType: (rec.recommendation_type as BookRecommendation['recommendationType']) || 'safe_bet',
            sourceBooks: rec.source_books || [],
            sessionId,
          }));

        // Separate classic from regular - prefer AI-generated classics
        const classicRec = recommendations.find(r => r.recommendationType === 'classic' && r.sourceBooks?.length > 0) 
          || recommendations.find(r => r.recommendationType === 'classic');
        const regularRecs = recommendations.filter(r => r.recommendationType !== 'classic').slice(0, limit - 1);

        // Mark these as shown (async, don't await)
        const shownIds = cachedRecs.slice(0, regularRecs.length + (classicRec ? 1 : 0)).map(r => r.id);
        supabase
          .from('user_cached_recommendations')
          .update({ status: 'shown', shown_at: new Date().toISOString() })
          .in('id', shownIds)
          .then(({ error }) => {
            if (error) console.error('Failed to mark as shown:', error);
          });

        // Trigger background generation if running low (< 10 remaining)
        const remainingCount = cacheCount - shownIds.length;
        if (remainingCount < 10) {
          console.log(`⚠️ Low cache (${remainingCount} remaining), triggering background generation`);
          triggerBackgroundGeneration(session.access_token);
        }

        return {
          recommendations: regularRecs,
          classicRecommendation: classicRec || null,
          fromCache: true,
          cacheCount: remainingCount
        };
      }

      // No cached recs - trigger generation and return empty
      console.log('📭 No cached recommendations, triggering generation...');
      triggerBackgroundGeneration(session.access_token);

      return {
        recommendations: [],
        classicRecommendation: null,
        fromCache: false,
        cacheCount: 0
      };
    },
    staleTime: 1000 * 60 * 1, // 1 minute - shorter for better responsiveness
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: true, // Refetch on mount to get latest
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Refresh function that forces new generation and refetch
  const refresh = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Invalidate cache first so UI shows loading
    await queryClient.invalidateQueries({ queryKey: ['cachedRecommendations'] });
    
    // Then trigger new generation in background
    triggerBackgroundGeneration(session.access_token);
    
    // Also invalidate the fallback recommendations
    await queryClient.invalidateQueries({ queryKey: ['bookRecommendations'] });
  };

  return {
    ...query,
    refresh,
  };
};

async function triggerBackgroundGeneration(accessToken: string) {
  try {
    const { error } = await supabase.functions.invoke('generate-cached-recommendations', {
      body: { triggerType: 'low_cache', targetCount: 15 },
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    
    if (error) {
      console.error('Background generation failed:', error);
    } else {
      console.log('✅ Background generation triggered');
    }
  } catch (e) {
    console.error('Failed to trigger background generation:', e);
  }
}
